import { apiClient } from './api'

export interface BatchJobMetadata {
  fieldMappings?: Record<string, number>
  validationIssues?: Array<{
    row: number
    field: string
    issue: string
  }>
  skippedRows?: number
  providerId?: string
  dealershipId?: string
  fileName?: string
  [key: string]: any
}

export interface BatchJob {
  id: string
  name: string
  type: string
  status: 'running' | 'completed' | 'failed' | 'scheduled' | 'paused'
  progress: number
  startedAt: string
  completedAt?: string
  duration?: number
  recordsProcessed?: number
  totalRecords?: number
  successCount?: number
  failureCount?: number
  error?: string
  errorStack?: string
  schedule?: string
  metadata?: BatchJobMetadata
}

export interface BatchJobFilters {
  jobType?: string
  status?: 'RUNNING' | 'COMPLETED' | 'FAILED' | 'SCHEDULED' | 'PAUSED'
  limit?: number
}

export const batchJobApi = {
  /**
   * Get batch jobs with optional filtering
   * Backend endpoint: GET /batch-jobs?jobType={type}&status={status}&limit={limit}
   */
  async getBatchJobs(filters?: BatchJobFilters): Promise<BatchJob[]> {
    try {
      const params: Record<string, string> = {}
      if (filters?.jobType) params.jobType = filters.jobType
      if (filters?.status) params.status = filters.status
      if (filters?.limit) params.limit = String(filters.limit)

      const jobs = await apiClient.get<BatchJob[]>(
        '/batch-jobs',
        Object.keys(params).length > 0 ? params : undefined
      )
      return jobs
    } catch (error) {
      console.error('Failed to fetch batch jobs:', error)
      throw new Error('Failed to fetch batch jobs')
    }
  },

  /**
   * Start an inventory import of every SFTP file, regardless of when the last scheduled sync ran.
   * Runs in the background as an inventory_backfill job.
   * Backend endpoint: POST /batch-jobs/backfill-inventory
   */
  async backfillInventory(): Promise<{ jobId: string }> {
    return apiClient.post<{ jobId: string }>('/batch-jobs/backfill-inventory')
  },

  /**
   * Get a specific batch job by ID
   * Backend endpoint: GET /batch-jobs/:jobId
   */
  async getBatchJob(jobId: string): Promise<BatchJob> {
    try {
      const job = await apiClient.get<BatchJob>(`/batch-jobs/${jobId}`)
      if (!job) {
        throw new Error('Batch job not found')
      }
      return job
    } catch (error) {
      console.error(`Failed to fetch batch job ${jobId}:`, error)
      throw new Error(`Failed to fetch batch job: ${jobId}`)
    }
  },
}

